/**
 * ÜCRETLİ KABİLİYETLER — cihaz tarafı kuralları (gelir modeli, kullanıcı kararı
 * 20.10.2026; `claude/notlar/2026-10-26-gelir-modeli-kararlar.md`).
 *
 * Kasada (şifreli kap, eşitlenen ayarlar) duranlar:
 *   - `kasaKimligi`        sertifikanın bağlandığı kimlik; kasa kodundan ayrı.
 *                          İlk aktivasyonda üretilir (kurulumda değil: iki
 *                          cihaz eşitlenmeden ayrı kimlik üretmesin).
 *   - `sertifika.<no>`     her sertifika AYRI anahtar: eşitleme anahtar başına
 *                          "son yazan kazanır"; tek anahtarda iki cihazın aynı
 *                          anda eklediği sertifikadan biri kaybolurdu.
 *   - `kabiliyet.sonTeyit` son sunucu teyidi (an + açık kabiliyetler + bitiş):
 *                          internetsizken 48 saat bununla sürülür. Her
 *                          yenilemede değil, değişince ya da 12 saatte bir
 *                          yazılır — her yazım bulut yedeğini tetikler.
 *
 * Ne açık olduğuna yalnızca sözleşmenin `sinirlar` fonksiyonu karar verir;
 * buradaki tek iş, jetonun / son teyidin hangisinin geçerli olduğunu seçmek.
 */

import {
  JETON_ONEKI,
  KASA_KIMLIGI_UZUNLUGU,
  OTURUM_SURELERI,
  SERTIFIKA_ONEKI,
  jetonGovdesiDenetle,
  kasaKimligiGecerli,
  muhurluAyristir,
  rastgeleKod,
  sertifikaGovdesiDenetle,
  sinirlar,
  type EtkinKabiliyetler,
  type JetonGovdesi,
  type SertifikaGovdesi,
  type Sinirlar,
} from "@parafon/contracts/sertifika";
import { ayGeri } from "../lib/tarihDonemleri";
import { M } from "../dil";

export const KASA_KIMLIGI_AYARI = "kasaKimligi";
export const SERTIFIKA_AYAR_ONEKI = "sertifika.";
export const SON_TEYIT_AYARI = "kabiliyet.sonTeyit";

/** Son teyit değişmediyse en çok bu aralıkta bir yeniden yazılır. */
export const SON_TEYIT_YAZMA_MS = 12 * 3_600_000;
export const CEVRIMDISI_MS = OTURUM_SURELERI.cevrimdisiSaat * 3_600_000;

interface AyarDeposu {
  readonly settings: {
    get(key: string): Promise<string | null>;
    set(key: string, value: string): Promise<void>;
    all(): Promise<Readonly<Record<string, string>>>;
  };
}

export interface KasaSertifikasi {
  readonly metin: string;
  readonly govde: SertifikaGovdesi;
}

/** Kasadaki geçerli biçimli sertifikalar (mühürü kapı doğrular; burada yalnızca biçim). */
export async function sertifikalariOku(repo: AyarDeposu): Promise<KasaSertifikasi[]> {
  const hepsi = await repo.settings.all();
  const sonuc: KasaSertifikasi[] = [];
  for (const [anahtar, metin] of Object.entries(hepsi)) {
    if (!anahtar.startsWith(SERTIFIKA_AYAR_ONEKI)) continue;
    const p = muhurluAyristir(metin, SERTIFIKA_ONEKI);
    if (p === null || sertifikaGovdesiDenetle(p.govde) !== null) continue;
    sonuc.push({ metin, govde: p.govde as SertifikaGovdesi });
  }
  return sonuc.sort((a, b) => (a.govde.basim < b.govde.basim ? -1 : a.govde.basim > b.govde.basim ? 1 : 0));
}

export async function kasaKimligiOku(repo: AyarDeposu): Promise<string | null> {
  const k = await repo.settings.get(KASA_KIMLIGI_AYARI);
  return kasaKimligiGecerli(k) ? k : null;
}

/** Yoksa üretir ve kasaya yazar (yalnızca aktivasyonda çağrılır). */
export async function kasaKimligiAl(repo: AyarDeposu): Promise<string> {
  const var_ = await kasaKimligiOku(repo);
  if (var_ !== null) return var_;
  const yeni = rastgeleKod(KASA_KIMLIGI_UZUNLUGU);
  await repo.settings.set(KASA_KIMLIGI_AYARI, yeni);
  return yeni;
}

/**
 * Kapının bastığı sertifikayı kasaya yazar. Biçimi bozuk ya da başka kasaya
 * basılmış sertifika YAZILMAZ (sebebiyle hata).
 */
export async function sertifikaKaydet(repo: AyarDeposu, metin: unknown, kasaKimligi: string): Promise<SertifikaGovdesi> {
  if (typeof metin !== "string") throw new Error(M.ayarlar.abonelik.hata.sertifikaYok);
  const p = muhurluAyristir(metin, SERTIFIKA_ONEKI);
  if (p === null) throw new Error(M.ayarlar.abonelik.hata.sertifikaOkunamadi);
  const sebep = sertifikaGovdesiDenetle(p.govde);
  if (sebep !== null) throw new Error(M.ayarlar.abonelik.hata.sertifikaGecersiz(sebep));
  const g = p.govde as SertifikaGovdesi;
  if (g.kasaKimligi !== kasaKimligi) throw new Error(M.ayarlar.abonelik.hata.baskaKasa);
  await repo.settings.set(`${SERTIFIKA_AYAR_ONEKI}${g.no}`, metin);
  return g;
}

/** Oturuma gidecek sertifikalar: yalnızca bu kasanın kimliğine basılmış olanlar. */
export function oturumSertifikalari(s: readonly KasaSertifikasi[], kasaKimligi: string | null): string[] {
  const kimlik = kasaKimligi ?? s[0]?.govde.kasaKimligi ?? null;
  return s.filter((x) => x.govde.kasaKimligi === kimlik).map((x) => x.metin);
}

// ---------------------------------------------------------------------------
// Jeton ve son teyit
// ---------------------------------------------------------------------------

export interface Teyit {
  /** Sunucunun teyit ettiği an (ISO). */
  readonly an: string;
  readonly kabiliyetler: EtkinKabiliyetler;
  readonly bitis: string | null;
}

/** Jetonun gövdesi (mühürünü fiyat sunucusu doğrular; cihaz kapıya HTTPS ile güvenir). */
export function jetonOku(jeton: unknown): JetonGovdesi | null {
  if (typeof jeton !== "string") return null;
  const p = muhurluAyristir(jeton, JETON_ONEKI);
  if (p === null || jetonGovdesiDenetle(p.govde) !== null) return null;
  return p.govde as JetonGovdesi;
}

export function teyitOku(deger: string | null): Teyit | null {
  if (deger === null) return null;
  try {
    const t = JSON.parse(deger) as Partial<Teyit>;
    if (typeof t.an !== "string" || Number.isNaN(Date.parse(t.an))) return null;
    const sahte: unknown = { surum: 1, muhur: "test", kabiliyetler: t.kabiliyetler, bitis: t.bitis ?? null, son: t.an };
    if (jetonGovdesiDenetle(sahte) !== null) return null;
    return { an: t.an, kabiliyetler: t.kabiliyetler as EtkinKabiliyetler, bitis: t.bitis ?? null };
  } catch {
    return null;
  }
}

/** Son teyit yazılmalı mı: içerik değiştiyse ya da 12 saat geçtiyse. */
export function teyitYazilmali(eski: Teyit | null, yeni: Teyit): boolean {
  if (eski === null) return true;
  if (JSON.stringify(eski.kabiliyetler) !== JSON.stringify(yeni.kabiliyetler) || eski.bitis !== yeni.bitis) return true;
  return Date.parse(yeni.an) - Date.parse(eski.an) >= SON_TEYIT_YAZMA_MS;
}

// ---------------------------------------------------------------------------
// Durum
// ---------------------------------------------------------------------------

/**
 * Ekranın gördüğü hâl. Kilitli kabiliyet bir anormallik DEĞİLDİR (CLAUDE.md
 * §6): bu hâllerin hiçbiri uyarı işareti ya da yargı rengi almaz.
 */
export type KabiliyetHali =
  | "yukleniyor"
  /** Kasada sertifika yok: ücretsiz. */
  | "sertifikasiz"
  /** Bu oturumda kapıya bağlanılmıyor (geliştirme sahtesi, merkez kapalı). */
  | "kapi-yok"
  /** Sunucu az önce teyit etti. */
  | "sunucu"
  /** Sunucuya ulaşılamıyor; son teyit 48 saatten yeni. */
  | "son-teyit"
  /** Sunucuya ulaşılamıyor; son teyit 48 saatten eski (ya da hiç yok). */
  | "teyit-eskidi"
  /** Sertifikaların süresi dolmuş ya da iptal edilmiş. */
  | "suresi-doldu"
  /** Oturum sınırı: bu kasa başka bir cihazda açıldı, buradaki oturum düştü. */
  | "baska-cihazda"
  /** Kapı sertifikaları reddetti (bozuk, başka mühür). */
  | "gecersiz";

export interface KabiliyetDurumu {
  readonly hal: KabiliyetHali;
  readonly sinirlar: Sinirlar;
  readonly kabiliyetler: EtkinKabiliyetler;
  /** Açık olanların en erken bitiş günü; `null` = süresiz ya da ücretsiz. */
  readonly bitis: string | null;
  /** Son sunucu teyidi (ISO), biliniyorsa. */
  readonly sonTeyit: string | null;
}

const UCRETSIZ = sinirlar({});

export function ucretsizDurum(hal: KabiliyetHali, sonTeyit: string | null = null): KabiliyetDurumu {
  return { hal, sinirlar: UCRETSIZ, kabiliyetler: {}, bitis: null, sonTeyit };
}

export function teyittenDurum(hal: "sunucu" | "son-teyit", t: Teyit): KabiliyetDurumu {
  return { hal, sinirlar: sinirlar(t.kabiliyetler), kabiliyetler: t.kabiliyetler, bitis: t.bitis, sonTeyit: t.an };
}

/**
 * Sunucuya ulaşılamayınca: son teyit 48 saatten yeniyse onunla sürülür,
 * değilse ücretli kabiliyetler kapanır (kayıtlar silinmez).
 */
export function cevrimdisiDurum(t: Teyit | null, simdi: Date): KabiliyetDurumu {
  if (t === null || Object.keys(t.kabiliyetler).length === 0) return ucretsizDurum("teyit-eskidi", t?.an ?? null);
  if (simdi.getTime() - Date.parse(t.an) >= CEVRIMDISI_MS) return ucretsizDurum("teyit-eskidi", t.an);
  return teyittenDurum("son-teyit", t);
}

/** Ücretsizde Günlük Analiz'de en çok kaç hesap karşılaştırılır (karar 20.10.2026). */
export const UCRETSIZ_KARSILASTIRMA = 2;

/**
 * Fiyat geçmişi derinliğine göre seçilebilecek en eski gün; `null` sınır
 * (sınırsız) → `undefined`. "Son N yıl" dönemiyle aynı gün (aynı ay aritmetiği).
 */
export function enEskiGun(fiyatGecmisiYil: number | null, bugun: string): string | undefined {
  return fiyatGecmisiYil === null ? undefined : ayGeri(bugun, 12 * fiyatGecmisiYil);
}

/**
 * Yedek hedefinin kilidi (gelir modeli: ücretsizde masaüstünde eşitlenen
 * klasör; 1. seviye kendi bulutu; 2. seviye Parafon sunucusu, 1.'yi de
 * içerir). Yalnızca YENİ hedef seçerken; geri yükleme ("Buluttan getir")
 * hiçbir zaman kilitli değil. `null` = serbest.
 */
export function hedefKilidi(tur: string, depolama: "klasor" | "bulut" | "sunucu"): string | null {
  if (tur === "parafon-sunucu") return depolama === "sunucu" ? null : M.ayarlar.kilit.sunucuYedegi;
  if (tur === "dropbox" || tur === "google-drive" || tur === "onedrive") {
    return depolama === "klasor" ? M.ayarlar.kilit.bulutYedegi : null;
  }
  return null;
}
